/**
 * What the shells paint while they cannot render themselves yet, and the boxes they share with that paint. Kept
 * apart from the shells because a `pendingComponent` is never code-split: everything imported here ships in the entry.
 * Why the shells are chunks at all: docs/notes/app/app-shell.md.
 */
import { useRouteContext, useRouter, useRouterState, type AnyRoute } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { BottomNavSkeleton, LogoSkeleton, NavSkeleton, UserMenuSkeleton, getChromeVariant } from './app-layout-skeleton'
import { useIsEmbedded } from './use-is-embedded'

/** The pathless layouts every page but the landing renders under. */
export type ShellId = '/_app' | '/_public' | '/_landing'

// A phone is a stage, not a page; desktop keeps the document scroll — docs/notes/app/app-shell.md.
export const SHELL_CLASS = 'flex flex-col max-md:h-dvh max-md:overflow-hidden md:min-h-dvh'
export const MAIN_CLASS = 'w-full min-h-0 flex-1'

export const TOPBAR_CLASS =
  'rule-fade-ends bg-background max-w-9xl mx-auto sticky top-0 z-50 flex h-18 w-full shrink-0 items-center justify-center px-2 md:px-4 print:hidden'

export function AppTopBarFallback() {
  return (
    <>
      <LogoSkeleton />
      <NavSkeleton />
      <UserMenuSkeleton />
    </>
  )
}

/** The bar's box with its fallback inside. Desktop only: on a phone a pending pane paints without a bar. */
function AppTopBarSkeleton() {
  return (
    <header className={cn(TOPBAR_CLASS, 'max-md:hidden')}>
      <div className="flex w-full items-center gap-2 md:gap-6">
        <AppTopBarFallback />
      </div>
    </header>
  )
}

// The iOS home indicator sits inside dvh, so the bar stands OFF it. As padding this instead grows the bar's own
// bordered card into that band: a 34px plate below the labels, outside the surface the bar looks like.
const OFF_THE_HOME_INDICATOR = 'calc(0.5rem + env(safe-area-inset-bottom))'

/**
 * The phone's section bar (app-bottom-nav.tsx): FLOATS over the foot of the pane, frosted, so a screen's content
 * scrolls on under it instead of stopping at its edge. The pane's scroller pads its end by the bar's slice
 * (`--chrome-bottomnav`, app.css) so the last row can still rise clear of it.
 */
export function BottomNavShell(props: { children: React.ReactNode }) {
  return (
    <div
      data-testid="app-bottom-nav"
      className="max-w-9xl bg-background/95 supports-backdrop-filter:bg-background/60 fixed inset-x-2 z-40 mx-auto rounded-xl border shadow-lg shadow-black/5 supports-backdrop-filter:backdrop-blur-xl supports-backdrop-filter:backdrop-saturate-150 md:hidden print:hidden"
      style={{ bottom: OFF_THE_HOME_INDICATOR }}
    >
      {props.children}
    </div>
  )
}

/**
 * The matched route's OWN pending: what the router would present for that lane — the OUTERMOST route under the shell
 * that declares one, as the router presents the first unresolved match, not the leaf (an index leaf declares none) —
 * so the shell's wait, the access guard's and the route's are one skeleton, and no lane of a shell ever spins.
 */
export function MatchedRoutePending(props: { shell: ShellId }) {
  const router = useRouter()
  const routeIds = useRouterState({ select: (s) => s.matches.map((m) => m.routeId) })
  const routes = router.routesById as unknown as Record<string, AnyRoute | undefined>
  const Pending = routeIds
    .slice(routeIds.indexOf(props.shell) + 1)
    .map((id) => routes[id]?.options.pendingComponent)
    .find((component) => component !== undefined)
  return Pending ? (
    <div className="flex h-full flex-col">
      {/* oxlint-disable-next-line react/static-components -- `Pending` is a LOOKUP of a route's declared
          `pendingComponent`, not a component defined inline; its identity is stable for a given route. */}
      <Pending />
    </div>
  ) : null
}

/**
 * The signed-in shell's pending, presented only while its chunk is on the wire — a first client entry from the public
 * pages or the landing. A mounted shell is retained across navigations, so it never pends again. The chrome's own
 * fallbacks around the route's skeleton, in the boxes the real chrome takes, so nothing moves when it lands.
 */
export function AppShellPending() {
  const routeId = useRouterState({ select: (s) => s.matches.at(-1)?.routeId ?? '' })
  // A route-transition preview renders inside the real chrome, so it paints the route's skeleton alone.
  const previewFrom = useRouteContext({ from: '__root__', select: (context) => context.previewFrom })
  const embedded = useIsEmbedded()
  const pending = <MatchedRoutePending shell="/_app" />
  if (previewFrom !== undefined || getChromeVariant(routeId) !== 'full') return pending
  return (
    <div className={SHELL_CLASS} data-app-layout={embedded ? 'bare' : ''}>
      {!embedded && <AppTopBarSkeleton />}
      <main className={MAIN_CLASS}>
        <div className="max-w-9xl mx-auto h-full w-full">{pending}</div>
      </main>
      {!embedded && (
        <BottomNavShell>
          <BottomNavSkeleton />
        </BottomNavShell>
      )}
    </div>
  )
}

/** The public shell draws no chrome of its own, so while its chunk loads the route's skeleton is the whole page. */
export function PublicShellPending() {
  return <MatchedRoutePending shell="/_public" />
}

/** The legal and docs pages: their nav and footer arrive with the shell's chunk, so the page's skeleton alone. */
export function LandingShellPending() {
  return <MatchedRoutePending shell="/_landing" />
}
